import React from 'react';
import PropTypes from 'prop-types';
import { LuInfo } from 'react-icons/lu';

// Color themes mirror the legacy JobEnteredSummary gradient sub-cards.
export const TILE_THEMES = {
  indigo:  { icon: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', tintFrom: 'rgba(102,126,234,0.05)', tintTo: 'rgba(102,126,234,0.02)', border: 'rgba(102,126,234,0.1)', value: 'tw-text-blue-600' },
  blue:    { icon: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', tintFrom: 'rgba(59,130,246,0.05)',  tintTo: 'rgba(59,130,246,0.02)',  border: 'rgba(59,130,246,0.1)',  value: 'tw-text-blue-600' },
  green:   { icon: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', tintFrom: 'rgba(16,185,129,0.05)',  tintTo: 'rgba(16,185,129,0.02)',  border: 'rgba(16,185,129,0.1)',  value: 'tw-text-green-600' },
  amber:   { icon: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', tintFrom: 'rgba(245,158,11,0.05)',  tintTo: 'rgba(245,158,11,0.02)',  border: 'rgba(245,158,11,0.1)',  value: 'tw-text-yellow-600' },
  rose:    { icon: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)', tintFrom: 'rgba(244,63,94,0.05)',   tintTo: 'rgba(244,63,94,0.02)',   border: 'rgba(244,63,94,0.1)',   value: 'tw-text-red-600' },
  cyan:    { icon: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', tintFrom: 'rgba(6,182,212,0.05)',   tintTo: 'rgba(6,182,212,0.02)',   border: 'rgba(6,182,212,0.1)',   value: 'tw-text-cyan-600' },
  violet:  { icon: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', tintFrom: 'rgba(139,92,246,0.05)',  tintTo: 'rgba(139,92,246,0.02)',  border: 'rgba(139,92,246,0.1)',  value: 'tw-text-violet-600' },
  slate:   { icon: 'linear-gradient(135deg, #64748b 0%, #475569 100%)', tintFrom: 'rgba(100,116,139,0.05)', tintTo: 'rgba(100,116,139,0.02)', border: 'rgba(100,116,139,0.1)', value: 'tw-text-slate-700' }
};

/**
 * StatTile — the shared dashboard KPI tile.
 *
 *   <StatTile icon={FaChartLine} theme="green" label="Win rate"
 *             value="54%" sub="of 120 quotes"
 *             trend={<YoY current={n} prev={p} />}   // node next to the value
 *             tooltip="Active projects only" />       // info icon + hover text
 *
 * `valueClassName` overrides the theme's value color (e.g. a conditional
 * red/green). All slots are optional; without them the tile renders exactly
 * as before.
 */
const StatTile = ({ icon: Icon, label, value, sub, theme, trend, tooltip, valueClassName }) => {
  const t = TILE_THEMES[theme] || TILE_THEMES.indigo;
  return (
    <div
      className="tw-rounded tw-p-3 tw-h-full"
      style={{
        background: `linear-gradient(135deg, ${t.tintFrom} 0%, ${t.tintTo} 100%)`,
        border: `1px solid ${t.border}`,
        transition: 'all 0.3s ease'
      }}
    >
      <div className="tw-flex tw-items-center tw-mb-2">
        <div
          className="tw-flex tw-items-center tw-justify-center tw-mr-2 tw-shrink-0"
          style={{ width: 30, height: 30, borderRadius: 8, background: t.icon }}
        >
          {Icon && <Icon className="tw-text-white" size={13} />}
        </div>
        <h6 className="tw-mb-0 tw-text-slate-700 tw-font-semibold tw-text-[11px] tw-uppercase tw-inline-flex tw-items-center tw-gap-1" style={{ letterSpacing: 0.2 }}>
          {label}
          {tooltip && (
            <span title={tooltip} className="tw-text-slate-400 tw-cursor-help tw-inline-flex" aria-label={tooltip}>
              <LuInfo size={11} />
            </span>
          )}
        </h6>
      </div>
      <div className="tw-flex tw-items-baseline tw-gap-2 tw-mb-1">
        <span
          className={`tw-font-bold ${valueClassName || t.value}`}
          style={{ fontSize: '1.4rem', lineHeight: 1.15, whiteSpace: 'nowrap' }}
        >
          {value}
        </span>
        {trend}
      </div>
      {sub && <span className="tw-text-slate-500 tw-text-[11px]">{sub}</span>}
    </div>
  );
};

StatTile.propTypes = {
  icon: PropTypes.elementType,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  sub: PropTypes.node,
  theme: PropTypes.oneOf(Object.keys(TILE_THEMES)),
  trend: PropTypes.node,
  tooltip: PropTypes.string,
  valueClassName: PropTypes.string
};

export default StatTile;
